/**
 * continueWfhWindow.ts — pure helpers for the Quick Links "Continue WFH"
 * permission window [leaving time, resuming time).
 *
 * The window may cross midnight (e.g. shift 14:00-22:00, filed at 22:05,
 * resuming 14:20 tomorrow); the backend reads end <= start as a roll-over. The
 * request is dated with today's calendar date because the backend's past-date
 * checks (no_past_dates policy, apply cut-off) reject an earlier date, so it is
 * NOT the shift-start day after midnight on an overnight shift; dating by
 * shift-start day would need the backend to follow the same rule first. All
 * functions take `now` and the shift as parameters so they are testable
 * without globals.
 */
import dayjs from 'dayjs';
import { format } from 'date-fns';

export interface ShiftWindow {
  startTime?: string | null;
  endTime?: string | null;
}

/** A row from the employee's shift plan (dates are YYYY-MM-DD). */
export interface ShiftPlanRow {
  startData?: string | null;
  endDate?: string | null;
  shiftType?: string | null;
}

/** A row from the shift-times list. */
export interface ShiftTimeEntry extends ShiftWindow {
  shiftType?: string | null;
}

/** Windows longer than this (after midnight roll-over) need explicit acknowledgement. */
export const CONTINUE_WFH_LONG_WINDOW_MINUTES = 16 * 60;

const hhmm = (t?: string | null): string => (t || '').slice(0, 5);

const toMinutes = (t: string): number => {
  const [h, m] = t.split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
};

const nowHHMM = (now: Date): string => format(now, 'HH:mm');

/** "hh:mm A" display of an HH:mm[:ss] time ('' when empty). */
export const formatClock = (time?: string | null): string => (time ? dayjs(`1970-01-01T${time}`).format('hh:mm A') : '');

/** True when the shift spans midnight (start later than end, e.g. 21:00-06:00). */
export function isOvernightShift(shiftStart?: string | null, shiftEnd?: string | null): boolean {
  const s = hhmm(shiftStart);
  const e = hhmm(shiftEnd);
  return !!s && !!e && s > e;
}

/**
 * The leaving time (HH:MM) for a new Continue WFH request: now, except before
 * the shift has started (day shift, or the pre-shift gap of an overnight
 * shift), where it is the shift start — time before the shift is not working time.
 */
export function wfhLeavingTime(now: Date, shift?: ShiftWindow | null): string {
  const current = nowHHMM(now);
  const s = hhmm(shift?.startTime);
  const e = hhmm(shift?.endTime);
  if (!s || !e) return current;
  if (s < e) return current < s ? s : current;
  if (s > e) return current >= e && current < s ? s : current;
  return current;
}

/** Minutes from start to end with midnight roll-over (end <= start adds 24h). */
export function windowMinutes(start: string, end: string): number {
  const s = toMinutes(hhmm(start));
  let e = toMinutes(hhmm(end));
  if (e <= s) e += 24 * 60;
  return e - s;
}

/** True when the window rolls over midnight (end strictly earlier than start). */
export function crossesMidnight(start: string, end: string): boolean {
  return hhmm(end) < hhmm(start);
}

/**
 * The leaving time (HH:MM) to use for a Continue WFH request. A new request
 * uses `wfhLeavingTime`. In update mode the stored start is kept so an edit
 * does not move the window's start, except for legacy rows.
 *
 * The legacy detection is a HEURISTIC: requests filed before the fix stored the
 * shift END as start_time, so `storedStart === shift end` is treated as the
 * legacy shape and the leaving time is recomputed (otherwise such a pending
 * row would stay stuck with a bogus window). Known misfires: (1) a post-fix
 * request filed exactly at the shift-end minute looks legacy; (2) a legacy row
 * whose shift has since changed is not recognised. Either way an edit moves the
 * start to the edit time even if only the reason changed. Acceptable for a
 * hotfix.
 */
export function resolveWfhLeavingTime(args: {
  now: Date;
  shift?: ShiftWindow | null;
  isUpdate: boolean;
  storedStart?: string | null;
}): string {
  const { now, shift, isUpdate, storedStart } = args;
  if (isUpdate && storedStart) {
    const isLegacyShiftEnd = hhmm(storedStart) === hhmm(shift?.endTime);
    if (!isLegacyShiftEnd) return hhmm(storedStart);
  }
  return wfhLeavingTime(now, shift);
}

/** The "resume equals leaving" message ('' when valid or no resume yet). */
export function wfhResumeMismatchError(leaving: string, resume: string): string {
  return resume && hhmm(resume) === leaving ? `Must differ from the leaving time (${formatClock(leaving)})` : '';
}

export interface WfhSubmitCheck {
  /** Leaving time (HH:MM) computed from `now` at submit time. */
  start: string;
  /** Message to show on the resume field; '' when the submit may proceed. */
  error: string;
  /** Overnight or over-16h window: confirm with the user first. */
  needsConfirm: boolean;
}

/**
 * Submit-time check for a Continue WFH request. Recomputes the leaving time
 * from `now` (the render-time value can be stale) and refuses resume ===
 * leaving, or a window over 16h that was not acknowledged.
 */
export function checkWfhSubmit(args: {
  now: Date;
  shift?: ShiftWindow | null;
  isUpdate: boolean;
  storedStart?: string | null;
  resume: string;
  longAcknowledged: boolean;
}): WfhSubmitCheck {
  const { now, shift, isUpdate, storedStart, resume, longAcknowledged } = args;
  const start = resolveWfhLeavingTime({ now, shift, isUpdate, storedStart });
  let error = wfhResumeMismatchError(start, resume);
  const hasResume = !!hhmm(resume);
  const long = !error && hasResume && windowMinutes(start, resume) > CONTINUE_WFH_LONG_WINDOW_MINUTES;
  if (!error && long && !longAcknowledged) {
    error = 'This window is longer than 16 hr. Please tick the confirmation to continue.';
  }
  const needsConfirm = !error && hasResume && (crossesMidnight(start, resume) || long);
  return { start, error, needsConfirm };
}

/**
 * The shift type in force for an employee right now, from their shift plan.
 * A plan covers date d when startData <= d <= (endDate || startData). After
 * midnight on an overnight shift whose plan covered yesterday, the shift still
 * in progress (yesterday's) wins over the new day's entry. Returns null when
 * no plan applies (the caller falls back to the general shift).
 */
export function activeShiftType(
  plans: ShiftPlanRow[] | null | undefined,
  shiftTimes: ShiftTimeEntry[] | null | undefined,
  now: Date
): string | null {
  const rows = plans || [];
  const covering = (d: string) => rows.find((p) => !!p.startData && !!p.shiftType && p.startData <= d && (p.endDate || p.startData) >= d);

  const today = format(now, 'yyyy-MM-dd');
  const yesterday = format(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1), 'yyyy-MM-dd');

  const prev = covering(yesterday);
  if (prev) {
    const times = (shiftTimes || []).find((t) => t.shiftType === prev.shiftType);
    if (times && isOvernightShift(times.startTime, times.endTime) && nowHHMM(now) < hhmm(times.endTime)) {
      return prev.shiftType as string;
    }
  }
  return covering(today)?.shiftType ?? null;
}
